import { FC, createContext, useContext, useMemo } from 'react';

import { v4 } from 'uuid';
import { differenceWith, isEqual } from 'lodash';

import { useTickets } from './tickets';
import { IConsolidateItem } from '../interfaces/IConsolidateItem';

interface IConsolidateContextData {
  consolidatedItems: IConsolidateItem[];
}

const ConsolidateContext = createContext({} as IConsolidateContextData);

const ConsolidateProvider: FC = ({ children }) => {
  const { tickets } = useTickets();

  const consolidatedItems = useMemo(() => {
    const newArr: IConsolidateItem[] = [];

    tickets.forEach((order) => {
      order.items.forEach((item) => {
        if (item.subitems && item.subitems.length > 0) {
          item.subitems.forEach((comboItem) => {
            const findComboItemIndex = newArr.findIndex(
              (comboItemI) => comboItemI.name === comboItem.name
            );

            if (findComboItemIndex >= 0) {
              newArr[findComboItemIndex].quantity += comboItem.quantity;
            } else {
              newArr.push({
                _id: v4(),
                name: comboItem.name,
                quantity: comboItem.quantity,
                note: comboItem.observations,
              });
            }
          });
        } else {
          const findItemIndex = newArr.findIndex(
            (itemI) =>
              itemI.name === item.name &&
              (!!itemI.note || !!item.observations
                ? itemI.note === item.observations
                : true) &&
              !(
                differenceWith(
                  item.additionals,
                  itemI.additionals || [],
                  isEqual
                )?.length > 0
              )
          );

          if (findItemIndex >= 0) {
            newArr[findItemIndex].quantity += item.quantity;
          } else {
            newArr.push({
              _id: v4(),
              name: item.name,
              quantity: item.quantity,
              note: item.observations,
              ...(item.additionals && {
                additionals: item.additionals.map((adt) => ({
                  ...adt,
                  _id: adt.id,
                })),
              }),
            });
          }
        }
      });
    });

    return newArr;
  }, [tickets]);

  return (
    <ConsolidateContext.Provider value={{ consolidatedItems }}>
      {children}
    </ConsolidateContext.Provider>
  );
};

const useConsolidate = (): IConsolidateContextData => {
  return useContext(ConsolidateContext);
};

export { ConsolidateProvider, useConsolidate };
